import React from "react";
import { TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";

import { useGetTeams } from "../../queries/useTeams";
import { userTypes } from "../../utils/constants";
import { leagueTypes } from "../../lib/constants";

/**
 * TeamAutocomplete Component
 * A reusable autocomplete component for team selection that groups teams by leagues
 *
 * @param {Object} props
 * @param {string|number} props.value - Current selected team ID
 * @param {function} props.onChange - Function to call when selection changes, receives team ID
 * @param {string} props.id - Unique ID for the component
 * @param {Object} props.style - Additional styles to apply
 * @returns {React.Component} - Rendered component
 */
const TeamAutocomplete = ({
  value,
  onChange,
  id = "team-autocomplete",
  style = {},
  userType = null,
}) => {
  // Get teams data using the custom hook
  const { data: teams = [] } = useGetTeams();

  // Find the team object that matches the current value (if any)
  const selectedTeam = value ? teams.find((team) => team.id === value) : null;

  const allTeams = userType
    ? teams.filter((team) =>
        userType === userTypes.COLLEGE
          ? team.league === leagueTypes.COLLEGE
          : team.league !== leagueTypes.COLLEGE
      )
    : teams;

  return (
    <Autocomplete
      id={id}
      options={allTeams}
      getOptionLabel={(option) => {
        return typeof option === "string"
          ? option
          : `${option.city} - ${option.name}` || "";
      }}
      filterOptions={(options, state) => {
        const inputValue = state.inputValue.toLowerCase();
        return options.filter(
          (option) =>
            option.name?.toLowerCase().includes(inputValue) ||
            option.city?.toLowerCase().includes(inputValue)
        );
      }}
      groupBy={(option) => (option.league || "Other").toUpperCase()}
      renderOption={(props, option) => {
        return (
          <>
            {typeof props === "string"
              ? props
              : `${props.city} - ${props.name}`}
          </>
        );
      }}
      value={selectedTeam}
      onChange={(event, newValue) => {
        onChange(newValue ? newValue.id : "");
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Team/School"
          variant="outlined"
          fullWidth
        />
      )}
      style={{ marginBottom: "1rem", ...style }}
    />
  );
};

export default TeamAutocomplete;
